import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { truckingReportExtAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export default function DailyTripReportPage() {
  const [dateFrom, setDateFrom] = useState(startOfMonth());
  const [dateTo,   setDateTo]   = useState(today());
  const [plateNo,  setPlateNo]  = useState('');
  const [rows,     setRows]     = useState([]);
  const [totals,   setTotals]   = useState(null);
  const [storeInfo, setStoreInfo] = useState({});
  const [loading,  setLoading]  = useState(false);
  const [loaded,   setLoaded]   = useState(false);
  const [error,    setError]    = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = { dateFrom, dateTo };
      if (plateNo.trim()) params.plateNo = plateNo.trim();
      const { data } = await truckingReportExtAPI.getDailyTripReport(params);
      setRows(data.data || []);
      setTotals(data.totals || {});
      setStoreInfo(data.storeInfo || {});
      setLoaded(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout pageTitle="Daily Trip Report">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-header { display: block !important; }
          @page { size: A4 landscape; margin: 8mm; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #999; padding: 2px 4px; font-size: 8pt; }
          thead { background: #f3f4f6; }
        }
        .print-header { display: none; }
      `}</style>

      {/* Print Header */}
      <div className="print-header mb-4">
        <p className="font-bold text-base">{storeInfo.companyName || 'ARVANNAH RENZ TRUCKING INC.'}</p>
        <p className="text-sm">{storeInfo.address || 'Q. Abeto St., Brgy. Abeto-Mirasol Taft South Manduriao, Iloilo City'}</p>
        <p className="font-bold text-lg mt-2">DAILY TRIP REPORT</p>
        <p className="text-sm">{plateNo ? `Truck: ${plateNo}` : 'All Trucks'}</p>
        <p className="text-sm">Period: {dateFrom} to {dateTo}</p>
      </div>

      {/* Filter Bar */}
      <div className="no-print bg-white border rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Plate No. (optional)</label>
          <input type="text" value={plateNo} onChange={e => setPlateNo(e.target.value)}
            placeholder="e.g. ABZ 1234"
            className="border rounded px-2 py-1.5 text-sm w-36" />
        </div>
        <button onClick={load} disabled={loading}
          className="bg-teal-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50">
          {loading ? 'Loading…' : 'Load Report'}
        </button>
        {loaded && (
          <button onClick={() => window.print()}
            className="bg-gray-600 text-white px-4 py-1.5 rounded text-sm">
            Print
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mb-3 no-print">{error}</p>}

      {/* Summary Strip */}
      {loaded && totals && (
        <div className="no-print grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {[
            { label: 'Total Trips', value: rows.length, raw: true },
            { label: 'Trucking Rate', value: `₱${PHP(totals.truckingRate)}` },
            { label: 'Stripping Fee', value: `₱${PHP(totals.strippingFee)}` },
            { label: "Add'l Stripping", value: `₱${PHP(totals.additionalStripping)}` },
            { label: 'Grand Total', value: `₱${PHP(totals.total)}` },
            { label: 'Rate (10%)', value: `₱${PHP(totals.rate10pct)}` },
          ].map(c => (
            <div key={c.label} className="bg-white border rounded-lg p-3">
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className="text-lg font-bold text-gray-800">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {loaded && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1 text-right">RATE</th>
                <th className="border px-2 py-1">WAYBILL NO.</th>
                <th className="border px-2 py-1">DATE</th>
                <th className="border px-2 py-1">CONSIGNEE</th>
                <th className="border px-2 py-1">DELIVERY ADD</th>
                <th className="border px-2 py-1">VAN NO.</th>
                <th className="border px-2 py-1 text-right">VAN SIZE</th>
                <th className="border px-2 py-1 text-right">TRUCKING RATE</th>
                <th className="border px-2 py-1 text-right">STRIPPING FEE</th>
                <th className="border px-2 py-1 text-right">ADD'L STRIPPING FEE</th>
                <th className="border px-2 py-1 text-right">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-6 text-gray-400 border">No records found.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-right">{PHP(r.rate10pct)}</td>
                  <td className="border px-2 py-1">{r.serialNo}</td>
                  <td className="border px-2 py-1 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="border px-2 py-1">{r.consignee}</td>
                  <td className="border px-2 py-1">{r.deliveryAddress}</td>
                  <td className="border px-2 py-1">{r.vanNo}</td>
                  <td className="border px-2 py-1 text-right">{r.vanSize}</td>
                  <td className="border px-2 py-1 text-right">₱{PHP(r.truckingRate)}</td>
                  <td className="border px-2 py-1 text-right">₱{PHP(r.strippingFee)}</td>
                  <td className="border px-2 py-1 text-right">
                    {r.additionalStripping > 0 ? `₱${PHP(r.additionalStripping)}` : '—'}
                  </td>
                  <td className="border px-2 py-1 text-right font-medium">₱{PHP(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {totals && rows.length > 0 && (
              <tfoot>
                <tr className="bg-gray-100 font-bold">
                  <td className="border px-2 py-1 text-right">{PHP(totals.rate10pct)}</td>
                  <td className="border px-2 py-1" colSpan={6}>TOTALS</td>
                  <td className="border px-2 py-1 text-right">{PHP(totals.truckingRate)}</td>
                  <td className="border px-2 py-1 text-right">{PHP(totals.strippingFee)}</td>
                  <td className="border px-2 py-1 text-right">{PHP(totals.additionalStripping)}</td>
                  <td className="border px-2 py-1 text-right">{PHP(totals.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Layout>
  );
}
